import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoCheckmarkCircle, IoArrowForward } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import ToriiTransition from "@/components/ToriiTransition";
import { useAuth } from "@/context/AuthContext";
import { getFormation } from "@/lib/api";
import { biomeFor } from "@/lib/biomes";

/**
 * Formation — le CHEMIN DES 7 PORTES d'une formation donnée (:fid).
 * Refonte cinématique : chaque porte est une carte immersive dont la
 * direction artistique (couleur, dégradé, motif) provient de son biome.
 */
export default function Formation() {
  const { fid } = useParams();
  const nav = useNavigate();
  const { loading, isAuthenticated, isAdmin } = useAuth();
  const [formation, setFormation] = useState(null);
  const [err, setErr] = useState(false);
  const [transition, setTransition] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: `/ecole/formation/${fid}` } } });
  }, [loading, isAuthenticated, nav, fid]);

  useEffect(() => { getFormation(fid).then(setFormation).catch(() => setErr(true)); }, [fid]);

  if (err) return <DojoShell crumbs={[{ label: "École", to: "/ecole" }]}><p style={{ color: "var(--text-secondary)" }}>Formation introuvable.</p></DojoShell>;
  if (!formation) return <DojoShell crumbs={[{ label: "École", to: "/ecole" }]}><p style={{ color: "var(--text-secondary)" }}>Ouverture du dojo…</p></DojoShell>;

  const gates = formation.gates || [];
  const doneCount = gates.filter((g) => g.progress?.completed).length;
  const openGate = (g) => { setTransition(g); };

  return (
    <DojoShell crumbs={[{ label: "École", to: "/ecole" }, { label: formation.title }]}>
      {transition && <ToriiTransition gate={transition} onDone={() => nav(`/ecole/porte/${transition.id}`)} />}

      {/* Bannière du parcours */}
      <div style={st.banner}>
        <span style={st.bannerKanji}>{formation.kanji || "門"}</span>
        <div style={st.bannerGlow} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={st.eyebrow}>LE CHEMIN DES {gates.length} PORTES</div>
          <h1 style={st.h1}>{formation.title}</h1>
          <p style={st.desc}>{formation.description}</p>
          <div style={st.pathProgress}>
            <div style={st.pathBar}><div style={{ ...st.pathFill, width: `${gates.length ? (doneCount / gates.length) * 100 : 0}%` }} /></div>
            <span style={st.pathTxt}>{doneCount} / {gates.length} portes franchies</span>
          </div>
        </div>
        {isAdmin && (
          <button style={st.adminBtn} data-testid="formation-mode-creation" onClick={() => nav("/ecole/creation")}>✎ Mode Création</button>
        )}
      </div>

      {/* Le chemin des portes */}
      <div style={st.path}>
        <div style={st.spine} aria-hidden />
        {gates.map((g, i) => {
          const b = biomeFor(g.biome || g.index || i + 1);
          const done = g.progress?.completed;
          const total = g.progress?.total || 0;
          const gdone = g.progress?.done || 0;
          const pct = total ? (gdone / total) * 100 : 0;
          const left = i % 2 === 0;
          return (
            <div key={g.id} style={{ ...st.row, justifyContent: left ? "flex-start" : "flex-end" }}>
              {/* Noeud sur la colonne vertébrale */}
              <span style={{ ...st.node, background: b.color, color: "#fff", boxShadow: `0 0 0 5px ${b.color}22, 0 6px 18px ${b.color}66` }}>
                {done ? <IoCheckmarkCircle size={20} /> : (g.index || i + 1)}
              </span>

              <button
                data-testid={`gate-card-${g.id}`}
                onClick={() => openGate(g)}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = `0 24px 60px ${b.color}55`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = st.card.boxShadow; }}
                style={{
                  ...st.card,
                  cursor: "pointer",
                  backgroundImage: `linear-gradient(150deg, ${b.color}1f, transparent 60%), ${b.env}`,
                  border: `1px solid ${b.color}55`,
                }}>
                {/* Motif / kanji du biome en filigrane */}
                <span style={{ ...st.cardMotif, color: b.color }}>{g.kanji || b.motif}</span>

                {/* Vignette */}
                <span style={{ ...st.thumb, backgroundImage: g.cover ? `url(${g.cover})` : "none", boxShadow: `inset 0 0 0 1.5px ${b.color}66` }}>
                  <span style={{ ...st.thumbScrim, background: `linear-gradient(160deg, ${b.color}33, rgba(0,0,0,0.6))` }} />
                  <span style={st.thumbKanji}>{g.kanji || b.motif}</span>
                </span>

                <div style={st.info}>
                  <div style={st.titleRow}>
                    <span style={st.title}>{g.title}</span>
                    {done && <span style={{ ...st.tag, color: "#22c55e", background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.4)" }}><IoCheckmarkCircle size={11} /> Réussie</span>}
                  </div>
                  <p style={st.gDesc}>{g.description}</p>
                  <div style={st.progRow}>
                    <div style={st.progBar}><div style={{ ...st.progFill, width: `${pct}%`, background: b.color }} /></div>
                    <span style={st.progTxt}>{gdone}/{total} leçons</span>
                  </div>
                </div>

                <span style={{ ...st.cta, background: b.color, color: "#fff" }}>
                  <IoArrowForward size={16} />
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </DojoShell>
  );
}

const st = {
  banner: { position: "relative", overflow: "hidden", marginBottom: 20, padding: "30px 28px", borderRadius: 24, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)", display: "flex", gap: 16, alignItems: "flex-start" },
  bannerKanji: { position: "absolute", right: -6, top: -26, fontSize: 150, fontWeight: 800, color: "var(--text-primary)", opacity: 0.05, lineHeight: 1, fontFamily: "var(--font-display)", pointerEvents: "none" },
  bannerGlow: { position: "absolute", top: -70, left: -30, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 20%, transparent), transparent 70%)" },
  eyebrow: { color: RED, fontWeight: 800, fontSize: 11.5, letterSpacing: 2.5 },
  h1: { fontSize: 32, fontWeight: 900, margin: "8px 0 6px", color: "var(--text-primary)", fontFamily: "var(--font-display)" },
  desc: { color: "var(--text-secondary)", fontSize: 14.5, margin: "0 0 16px", maxWidth: 640, lineHeight: 1.6 },
  pathProgress: { display: "flex", alignItems: "center", gap: 12, maxWidth: 460 },
  pathBar: { flex: 1, height: 8, borderRadius: 999, background: "var(--bg-tertiary)", overflow: "hidden" },
  pathFill: { height: "100%", background: RED, borderRadius: 999, transition: "width .5s ease" },
  pathTxt: { fontSize: 12.5, fontWeight: 700, color: "var(--text-secondary)", whiteSpace: "nowrap" },
  adminBtn: { position: "relative", zIndex: 1, marginLeft: "auto", flexShrink: 0, padding: "10px 16px", borderRadius: 11, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  hint: { display: "flex", alignItems: "center", gap: 12, background: "color-mix(in srgb, var(--brand) 8%, var(--bg-secondary))", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)", color: "var(--text-secondary)", padding: "14px 18px", borderRadius: 14, fontSize: 14, marginBottom: 26, lineHeight: 1.5 },

  path: { position: "relative", display: "flex", flexDirection: "column", gap: 22, paddingLeft: 8 },
  spine: { position: "absolute", left: 27, top: 10, bottom: 10, width: 3, borderRadius: 999, background: "linear-gradient(var(--border-c), var(--border-c))", zIndex: 0 },
  row: { position: "relative", display: "flex", alignItems: "center", paddingLeft: 56, minHeight: 128 },
  node: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, zIndex: 2, flexShrink: 0 },

  card: { position: "relative", overflow: "hidden", width: "min(560px, 100%)", display: "flex", alignItems: "center", gap: 18, textAlign: "left", padding: "18px 20px", borderRadius: 20, backgroundSize: "cover", backgroundPosition: "center", boxShadow: "0 12px 34px rgba(0,0,0,0.28)", transition: "transform .22s ease, box-shadow .22s ease" },
  cardMotif: { position: "absolute", right: -6, bottom: -30, fontSize: 120, fontWeight: 800, opacity: 0.12, lineHeight: 1, pointerEvents: "none", fontFamily: "var(--font-display)" },
  thumb: { position: "relative", width: 88, height: 88, borderRadius: 16, flexShrink: 0, backgroundSize: "cover", backgroundPosition: "center", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" },
  thumbScrim: { position: "absolute", inset: 0 },
  thumbKanji: { position: "relative", zIndex: 1, fontSize: 34, fontWeight: 800, color: "#fff", textShadow: "0 2px 12px rgba(0,0,0,0.8)", fontFamily: "var(--font-display)" },
  info: { flex: 1, minWidth: 0, position: "relative", zIndex: 1 },
  titleRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  title: { fontSize: 18.5, fontWeight: 900, color: "#fff", textShadow: "0 1px 8px rgba(0,0,0,0.6)" },
  tag: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 999 },
  gDesc: { color: "rgba(255,255,255,0.82)", fontSize: 13, margin: "6px 0 10px", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  progRow: { display: "flex", alignItems: "center", gap: 10 },
  progBar: { flex: 1, maxWidth: 200, height: 6, background: "rgba(255,255,255,0.18)", borderRadius: 999, overflow: "hidden" },
  progFill: { height: "100%", borderRadius: 999, transition: "width .4s ease" },
  progTxt: { fontSize: 11.5, color: "rgba(255,255,255,0.8)", fontWeight: 700, whiteSpace: "nowrap" },
  cta: { position: "relative", zIndex: 1, width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 6px 16px rgba(0,0,0,0.3)" },
};
